import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Drawer,
  AppBar,
  Toolbar,
  Tooltip,
  Divider,
} from '@mui/material';
import { Info, Business } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import COLORS from '../../../resources/style/Themes';
import { Routes } from '../../../enums/Routes';
import type {
  GroupEmergencyContactsOnAzure,
  OrgEmergencyContactsOnAzure,
} from '../../../../generated-client/models';

interface Group {
  group_id: string;
  group_name: string;
}

interface EmergencyResponseRouteBarProps {
  allGroups: Group[];
  setActiveGroup: (groupName: string) => void;
  activeGroup: string;
  emergencyContacts: GroupEmergencyContactsOnAzure[];
  orgEmergencyContacts: OrgEmergencyContactsOnAzure | null;
}

// Special ID to represent organization-level view
const ORG_LEVEL_ID = 'organization';

/**
 * Calculate the total number of responders for a group
 */
const getResponderCount = (
  groupId: string,
  emergencyContacts: GroupEmergencyContactsOnAzure[],
): number => {
  const contacts = emergencyContacts.find((c) => c.group_id === groupId);
  if (!contacts) return 0;

  const authIdCount = contacts.contact_auth_ids
    ? Object.keys(contacts.contact_auth_ids).length
    : 0;
  const emailCount = contacts.contact_email_addresses
    ? Object.keys(contacts.contact_email_addresses).length
    : 0;

  return authIdCount + emailCount;
};

/**
 * Calculate the total number of organization-level responders
 */
const getOrgResponderCount = (
  orgEmergencyContacts: OrgEmergencyContactsOnAzure | null,
): number => {
  if (!orgEmergencyContacts) return 0;

  const authIdCount = orgEmergencyContacts.contact_auth_ids
    ? Object.keys(orgEmergencyContacts.contact_auth_ids).length
    : 0;
  const emailCount = orgEmergencyContacts.contact_email_addresses
    ? Object.keys(orgEmergencyContacts.contact_email_addresses).length
    : 0;

  return authIdCount + emailCount;
};

/**
 * Sidebar navigation for Emergency Response Management
 */
const EmergencyResponseRouteBar: React.FC<EmergencyResponseRouteBarProps> = ({
  allGroups,
  setActiveGroup,
  activeGroup,
  emergencyContacts,
  orgEmergencyContacts,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const { t } = useTranslation();

  const handleGroupChange = (groupId: string): void => {
    if (groupId === ORG_LEVEL_ID) {
      setActiveGroup(ORG_LEVEL_ID);
      history.push(`${Routes.EMERGENCY_RESPONDER_MANAGEMENT}/organization`);
      return;
    }

    const group = allGroups.find((g) => g.group_id === groupId);
    if (group) {
      setActiveGroup(groupId);
      history.push(
        `${Routes.EMERGENCY_RESPONDER_MANAGEMENT}/${encodeURIComponent(groupId)}`,
      );
    }
  };

  const renderMobileView = () => (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: COLORS.WHITE,
        boxShadow: 0,
        top: 64,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ px: 2 }}>
        <FormControl fullWidth size="small">
          <Select
            value={activeGroup}
            onChange={(e) => handleGroupChange(e.target.value)}
            sx={{
              backgroundColor: COLORS.WHITE,
              '& .MuiSelect-select': {
                py: 1.5,
              },
            }}
          >
            <MenuItem value={ORG_LEVEL_ID}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business fontSize="small" />
                <Typography>
                  {t(StringKeys.COMPANY_WIDE_EMERGENCY_RESPONDERS)} (
                  {getOrgResponderCount(orgEmergencyContacts)}{' '}
                  {t(StringKeys.EMERGENCY_RESPONDERS_LABEL)})
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            {allGroups?.map((group) => (
              <MenuItem key={group.group_id} value={group.group_id}>
                <Typography>
                  {group.group_name} (
                  {getResponderCount(group.group_id, emergencyContacts)}{' '}
                  {t(StringKeys.EMERGENCY_RESPONDERS_LABEL)})
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Toolbar>
    </AppBar>
  );

  const renderDesktopView = () => (
    <Drawer
      variant="permanent"
      sx={{
        width: 240,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: 240,
          boxSizing: 'border-box',
          top: 65,
          background: '#ffffff',
          height: 'calc(100vh - 65px)',
          overflowY: 'auto',

          // Scrollbar styling
          '&::-webkit-scrollbar': {
            width: '6px', // scrollbar width
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a0a0a0',
          },

          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: '#c1c1c1 transparent',
        },
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#3e5371',
          boxShadow: 3,
          color: COLORS.WHITE,
          padding: '10px',
          paddingTop: '20px',
          paddingBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 300,
              textAlign: 'left',
            }}
          >
            {t(StringKeys.EMERGENCY_RESPONDER_MANAGEMENT)}
          </Typography>

          <Tooltip
            title={t(StringKeys.SELECT_A_GROUP_TO_MANAGE_EMERGENCY_RESPONSE)}
          >
            <IconButton size="small" sx={{ color: COLORS.LIGHT_GRAY, mt: 0.5 }}>
              <Info />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <List sx={{ marginTop: '0px', paddingTop: '0px' }}>
        {/* Organization-level option */}
        <ListItem
          component={Link}
          to={`${Routes.EMERGENCY_RESPONDER_MANAGEMENT}/organization`}
          onClick={() => setActiveGroup(ORG_LEVEL_ID)}
          sx={{
            mt: 2,
            color: '#3A3B3C',
            borderRadius: 2,
            backgroundColor:
              activeGroup === ORG_LEVEL_ID ? '#ededed' : '#ffffff',
            padding: '10px',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          }}
        >
          <Business sx={{ mr: 1.5, color: COLORS.SAFEGUARD_BLUE }} />
          <ListItemText
            primary={t(StringKeys.COMPANY_WIDE_EMERGENCY_RESPONDERS)}
            secondary={`${getOrgResponderCount(orgEmergencyContacts)} ${t(StringKeys.EMERGENCY_RESPONDERS_LABEL)}`}
          />
        </ListItem>

        {/* Divider */}
        <Divider sx={{ my: 2, mx: 2 }} />

        {/* Group options */}
        {allGroups?.map((group) => (
          <ListItem
            key={group.group_id}
            component={Link}
            to={`${Routes.EMERGENCY_RESPONDER_MANAGEMENT}/${encodeURIComponent(
              group.group_id,
            )}`}
            onClick={() => setActiveGroup(group.group_id)}
            sx={{
              mt: 2,
              color: '#3A3B3C',
              borderRadius: 2,
              backgroundColor:
                activeGroup === group.group_id ? '#ededed' : '#ffffff',

              padding: '10px',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <ListItemText
              primary={group.group_name}
              secondary={`${getResponderCount(group.group_id, emergencyContacts)} ${t(StringKeys.EMERGENCY_RESPONDERS_LABEL)}`}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  return isMobile ? renderMobileView() : renderDesktopView();
};

export default EmergencyResponseRouteBar;
